import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * List-page filter memory.
 *
 * A list page's filters survive opening a record and coming back (the detail
 * pages' Back buttons navigate to the bare list path, so URL params alone do
 * not), and are dropped when the operator opens a different sidebar menu or
 * lands on a login screen.
 *
 * Entries live in sessionStorage under `_zenvoya_list_filters:<listPath>:<name>`.
 * Every access is wrapped in try/catch — storage can be blocked, and a list
 * page must still render with its defaults when it is.
 */
const PREFIX = '_zenvoya_list_filters:';

const keyFor = (listPath, name) => `${PREFIX}${listPath}:${name}`;

export function readListFilter(listPath, name) {
  try {
    const raw = sessionStorage.getItem(keyFor(listPath, name));
    return raw == null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

export function writeListFilter(listPath, name, value) {
  try {
    sessionStorage.setItem(keyFor(listPath, name), JSON.stringify(value));
  } catch {
    /* storage blocked — filters just won't be remembered */
  }
}

/**
 * useState that remembers its value for `listPath`.
 *
 * `initial` is either a plain default, or a function `(saved) => value` for a
 * page that must merge the saved value with something else (a deep link, new
 * filter keys added since it was saved).
 */
export function useListFilter(listPath, name, initial) {
  const [value, setValue] = useState(() => {
    const saved = readListFilter(listPath, name);
    if (typeof initial === 'function') return initial(saved);
    return saved === undefined ? initial : saved;
  });
  useEffect(() => {
    writeListFilter(listPath, name, value);
  }, [listPath, name, value]);
  return [value, setValue];
}

/**
 * Called on every route change from the portal layouts. Opening a sidebar
 * menu (`pathname` is one of `menuPaths`) drops every list's filters except
 * that menu's own; a login screen drops them all. Anything else — a detail
 * page, a request opened from the Requests queue — keeps them.
 */
export function clearListFiltersOnMenuChange(pathname, menuPaths) {
  const isLogin = pathname.endsWith('/login');
  if (!isLogin && !menuPaths.includes(pathname)) return;
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(PREFIX))
      .filter((k) => isLogin || !k.startsWith(`${PREFIX}${pathname}:`))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* storage blocked — nothing to clear */
  }
}

/**
 * Mounted once at each portal root (it must see the login route too, which
 * sits outside the layouts). Child effects run before this one, so a list
 * that just mounted has already written its own entry — which the menu rule
 * keeps — by the time the others are cleared.
 */
export function useListFilterReset(sidebarConfig) {
  const { pathname } = useLocation();
  const menuPaths = useMemo(() => collectMenuPaths(sidebarConfig), [sidebarConfig]);
  useEffect(() => {
    clearListFiltersOnMenuChange(pathname, menuPaths);
  }, [pathname, menuPaths]);
}

/** Every `path` in a nested sidebar config (`items` / `children` arrays). */
export function collectMenuPaths(nodes, out = []) {
  (nodes || []).forEach((n) => {
    if (n.path) out.push(n.path);
    collectMenuPaths(n.items, out);
    collectMenuPaths(n.children, out);
  });
  return out;
}
